import React from 'react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { useInView } from 'react-intersection-observer';

import { Link, LinkProps } from '@opendoor/bricks/core';
import RegularContainer from '../../../RegularContainer';
import { useObservability } from '../../../../helpers/observability';

const FooterCol = styled.div`
  width: 25%;
  @media (max-width: 767px) {
    float: left;
    width: 100%;
    text-align: left;
  }
`;

const FooterColA: React.FC<{ label: string } & Pick<LinkProps, 'href' | 'style'>> = ({
  label,
  href,
  style,
  children,
}) => {
  const { trackEvent } = useObservability();
  return (
    <Link
      color="neutrals100"
      // TODO: add analyticsName and remove onClick
      analyticsName=""
      aria-label={label}
      href={href}
      style={style}
      onClick={() =>
        // TODO: remove categoryOverride
        // observability is initialized with 'cosmos' as the category for all events
        trackEvent('cta-click', label, undefined, {
          categoryOverride: 'homepage',
        })
      }
    >
      {children}
    </Link>
  );
};

const FooterColUL = styled.ul`
  padding: 0;
  margin: 10px 0 46px 0;
  list-style: none;
`;

const FooterColLI = styled.li`
  padding: 0;
  margin: 15px 0;
  font-size: 14px;
  line-height: 15px;
`;

const hiddenXSDown = css`
  @media (max-width: 767px) {
    display: none !important;
  }
`;

const hiddenMDUp = css`
  @media (min-width: 767px) {
    display: none !important;
  }
`;

const leftSpaceXs = css`
  @media (max-width: 767px) {
    margin-left: 25px;
  }
`;

const H6 = styled.h6`
  text-transform: uppercase;
  margin: 15px 0;
  font-size: 14px;
`;

const SocialIcons = styled.div`
  flex: 1;
  padding: 0 0 50px 0;
  text-align: center;

  @media (max-width: 767px) {
    margin: 15px 0;
    padding: 30px 0;
  }
`;

const SocialIconA = styled.a`
  display: inline-block;
  margin: 0 20px;
`;

const FooterSection = styled.footer`
  margin: 46px auto;
  max-width: 340px;

  @media (min-width: 767px) {
    max-width: 689px;
    margin: 89px auto;
  }

  @media (min-width: 992px) {
    max-width: 928px;
  }

  @media (min-width: 1200px) {
    max-width: 925px;
  }
`;

const FairHousing = styled.img`
  height: 24px;
  width: auto;
  opacity: 0.2;
  margin-top: 3px;
  margin-left: 14px;
  @media (max-width: 767px) {
    height: 25px;
    float: none;
  }
`;

const OpenDoorSpan = styled.span`
  font-weight: 400;
  font-size: 12px;
  line-height: 21.6px;
  color: #6e6e6e;
`;

const LegalP = styled.p`
  font-size: 12px;
  color: #6e6e6e;
`;

const copyRow = css`
  color: #526475;
  font-size: 14px;
  align-items: center;
  justify-content: center;
`;

interface IProps {
  hideLegalese?: boolean;
  addLegalese?: React.ReactNode;
}

const Footer: React.FC<IProps> = (props: IProps) => {
  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });
  return (
    <FooterSection ref={ref}>
      <RegularContainer
        className="test"
        style={{ display: 'flex', justifyContent: 'space-between', maxWidth: '100%' }}
      >
        <FooterCol>
          <H6>Sell your home</H6>
          <FooterColUL>
            <FooterColLI>
              <FooterColA label="sell-your-home-request-offer" href="https://www.opendoor.com/">
                Request an offer
              </FooterColA>
            </FooterColLI>
            <FooterColLI>
              <FooterColA label="sell-your-home-pricing" href="https://www.opendoor.com/w/pricing">
                Pricing
              </FooterColA>
            </FooterColLI>
            <FooterColLI>
              <FooterColA label="sell-your-home-reviews" href="https://www.opendoor.com/reviews">
                Reviews
              </FooterColA>
            </FooterColLI>
            <FooterColLI>
              <FooterColA label="sell-your-home-stories" href="https://www.opendoor.com/w/stories">
                Stories
              </FooterColA>
            </FooterColLI>
          </FooterColUL>
          <H6>Buy a home</H6>
          <FooterColUL>
            <FooterColLI>
              <FooterColA label="buy-a-home-buy" href="https://www.opendoor.com/w/homes">
                Buy
              </FooterColA>
            </FooterColLI>
          </FooterColUL>
          <H6>Partners</H6>
          <FooterColUL>
            <FooterColLI>
              <FooterColA
                label="partners-vendors"
                href="https://www.opendoor.com/w/vendor-partnerships"
              >
                Vendors
              </FooterColA>
            </FooterColLI>
            <FooterColLI>
              <FooterColA
                label="partners-builder"
                href="https://www.opendoor.com/w/partner/builder"
              >
                Builder
              </FooterColA>
            </FooterColLI>
          </FooterColUL>
          <H6 css={hiddenMDUp}>About Us</H6>
          <FooterColUL css={hiddenMDUp}>
            <FooterColLI>
              <FooterColA label="about-company" href="https://www.opendoor.com/w/about">
                Company
              </FooterColA>
            </FooterColLI>
            <FooterColLI>
              <FooterColA label="about-career" href="https://www.opendoor.com/jobs">
                Career
              </FooterColA>
            </FooterColLI>
            <FooterColLI>
              <FooterColA label="about-press" href="https://www.opendoor.com/press">
                Press
              </FooterColA>
            </FooterColLI>
            <FooterColLI>
              <FooterColA label="about-contact" href="https://www.opendoor.com/w/contact-us">
                Contact
              </FooterColA>
            </FooterColLI>
            <FooterColLI>
              <FooterColA label="about-investors" href="https://investor.opendoor.com/">
                Investors
              </FooterColA>
            </FooterColLI>
          </FooterColUL>
        </FooterCol>
        <FooterCol css={hiddenXSDown}>
          <H6>Buy and sell</H6>
          <FooterColUL>
            <FooterColLI>
              <FooterColA
                label="buy-and-sell-with-opendoor"
                href="https://www.opendoor.com/buy-and-sell"
              >
                Opendoor Trade-in
              </FooterColA>
            </FooterColLI>
            <FooterColLI>
              <FooterColA label="trade-in-builder" href="https://www.opendoor.com/w/builder">
                Builder trade-up
              </FooterColA>
            </FooterColLI>
          </FooterColUL>
          <H6>Agents</H6>
          <FooterColUL>
            <FooterColLI>
              <FooterColA
                label="agents-make-offer"
                href="https://www.opendoor.com/w/agents#alp-buy--form"
              >
                Make an offer
              </FooterColA>
            </FooterColLI>
            <FooterColLI>
              <FooterColA label="agents-request-offer" href="https://www.opendoor.com/w/agents">
                Request an offer
              </FooterColA>
            </FooterColLI>
          </FooterColUL>
          <H6>Resources</H6>
          <FooterColUL>
            <FooterColLI>
              <FooterColA label="resources-blog" href="https://www.opendoor.com/w/blog">
                Blog
              </FooterColA>
            </FooterColLI>
            <FooterColLI>
              <FooterColA label="resources-guides" href="https://www.opendoor.com/w/resources">
                Guides
              </FooterColA>
            </FooterColLI>
            <FooterColLI>
              <FooterColA label="resources-faq" href="https://www.opendoor.com/faq">
                FAQ
              </FooterColA>
            </FooterColLI>
            <FooterColLI>
              <FooterColA label="resources-help-center" href="https://help.opendoor.com">
                Help Center
              </FooterColA>
            </FooterColLI>
          </FooterColUL>
        </FooterCol>
        <FooterCol css={leftSpaceXs}>
          <H6 css={hiddenMDUp}>Buy and sell</H6>
          <FooterColUL css={hiddenMDUp}>
            <FooterColLI>
              <FooterColA
                label="buy-and-sell-with-opendoor"
                href="https://www.opendoor.com/buy-and-sell"
              >
                Opendoor Trade-in
              </FooterColA>
            </FooterColLI>
            <FooterColLI>
              <FooterColA label="trade-in-builder" href="https://www.opendoor.com/w/builder">
                Builder trade-up
              </FooterColA>
            </FooterColLI>
          </FooterColUL>
          <H6 css={hiddenMDUp}>Agents</H6>
          <FooterColUL css={hiddenMDUp}>
            <FooterColLI>
              <FooterColA
                label="agents-make-offer"
                href="https://www.opendoor.com/w/agents#alp-buy--form"
              >
                Make an offer
              </FooterColA>
            </FooterColLI>
            <FooterColLI>
              <FooterColA label="agents-request-offer" href="https://www.opendoor.com/w/agents">
                Request an offer
              </FooterColA>
            </FooterColLI>
          </FooterColUL>
          <H6 css={hiddenMDUp}>Resources</H6>
          <FooterColUL css={hiddenMDUp}>
            <FooterColLI>
              <FooterColA label="resources-blog" href="https://www.opendoor.com/w/blog">
                Blog
              </FooterColA>
            </FooterColLI>
            <FooterColLI>
              <FooterColA label="resources-guides" href="https://www.opendoor.com/w/resources">
                Guides
              </FooterColA>
            </FooterColLI>
            <FooterColLI>
              <FooterColA label="resources-faq" href="https://www.opendoor.com/faq">
                FAQ
              </FooterColA>
            </FooterColLI>
            <FooterColLI>
              <FooterColA label="resources-help-center" href="https://help.opendoor.com">
                Help Center
              </FooterColA>
            </FooterColLI>
          </FooterColUL>
          <H6 css={hiddenXSDown}>About Us</H6>
          <FooterColUL css={hiddenXSDown}>
            <FooterColLI>
              <FooterColA label="about-company" href="https://www.opendoor.com/w/about">
                Company
              </FooterColA>
            </FooterColLI>
            <FooterColLI>
              <FooterColA label="about-career" href="https://www.opendoor.com/jobs">
                Career
              </FooterColA>
            </FooterColLI>
            <FooterColLI>
              <FooterColA label="about-press" href="https://www.opendoor.com/press">
                Press
              </FooterColA>
            </FooterColLI>
            <FooterColLI>
              <FooterColA label="about-contact" href="https://www.opendoor.com/w/contact-us">
                Contact
              </FooterColA>
            </FooterColLI>
            <FooterColLI>
              <FooterColA label="about-investors" href="https://investor.opendoor.com/">
                Investors
              </FooterColA>
            </FooterColLI>
          </FooterColUL>
          <H6>Terms & privacy</H6>
          <FooterColUL>
            <FooterColLI>
              <FooterColA label="terms-trust-safety" href="https://www.opendoor.com/w/safety">
                Trust & Safety
              </FooterColA>
            </FooterColLI>
            <FooterColLI>
              <FooterColA label="terms-tos" href="https://www.opendoor.com/w/terms">
                Terms of Service
              </FooterColA>
            </FooterColLI>
            <FooterColLI>
              <FooterColA label="terms-privacy" href="https://www.opendoor.com/w/privacy">
                Privacy Policy
              </FooterColA>
            </FooterColLI>
            <FooterColLI>
              <FooterColA label="terms-do-not-sell" href="https://www.opendoor.com/privacyrequest">
                Do Not Sell My Info
              </FooterColA>
            </FooterColLI>
          </FooterColUL>
        </FooterCol>
        {inView && (
          <FooterCol css={hiddenXSDown}>
            <H6>Get the app</H6>
            <FooterColA
              label="app-download-android"
              href="https://play.google.com/store/apps/details?id=com.opendoor.buyerapp&hl=en"
              style={{ marginBottom: '10px', display: 'block' }}
            >
              <img
                src="https://imgdrop.imgix.net/3a52f003f27742f3b4384a3168352335.png"
                alt="Play store button"
                width={150}
                height={44}
              />
            </FooterColA>
            <FooterColA
              label="app-download-ios"
              href="https://itunes.apple.com/us/app/opendoor-homes-all-day-open/id1143708068?mt=8"
              style={{ marginBottom: '10px', display: 'block' }}
            >
              <img
                src="https://imgdrop.imgix.net/03f8e282af254a80ac28358e640319ab.svg"
                alt="App store button"
                width={150}
                height={50}
              />
            </FooterColA>
          </FooterCol>
        )}
      </RegularContainer>

      <RegularContainer flex>
        <SocialIcons>
          <SocialIconA href="https://www.facebook.com/OpendoorHQ">
            <img
              src="https://imgdrop.imgix.net/1ac79b6da72048e5ac4e3770a67d9f51.svg"
              width="13px"
              height="24px"
              alt="Facebook"
            />
          </SocialIconA>
          <SocialIconA href="https://twitter.com/Opendoor">
            <img
              src="https://imgdrop.imgix.net/f2641428da574ecf908264f2ab31a032.svg"
              height="23px"
              width="28px"
              alt="Twitter"
            />
          </SocialIconA>
          <SocialIconA href="https://www.linkedin.com/company/opendoor-com/">
            <img
              src="https://imgdrop.imgix.net/eb24a868176f4dc182c1f3de199481e7.svg"
              width="25px"
              height="23px"
              alt="Linkedin"
            />
          </SocialIconA>
        </SocialIcons>
        <a
          href="https://www.bbb.org/us/ca/san-francisco/profile/real-estate/opendoor-1116-539425/#sealclick"
          target="_blank"
          rel="nofollow noopener noreferrer"
        >
          <img
            src="https://seal-goldengate.bbb.org/seals/blue-seal-200-42-bbb-539425.png"
            alt="Opendoor BBB Business Review"
            style={{ display: 'block', margin: 'auto' }}
            width="1px"
            height="1px"
          />
        </a>
      </RegularContainer>

      <RegularContainer
        css={copyRow}
        style={{
          color: '#526475',
          fontSize: '14px',
          display: 'flex',
          margin: 0,
          width: '100%',
          maxWidth: '100%',
        }}
      >
        <OpenDoorSpan>&copy; {new Date().getFullYear()} Opendoor</OpenDoorSpan>
        <div
          css={css`
            margin-left: auto;
            display: flex;
          `}
        >
          <div>
            <svg
              width="73.75px"
              height="30px"
              viewBox="0 0 119 45"
              xmlns="http://www.w3.org/2000/svg"
              role="img"
            >
              <title>BBB Accredited Business</title>
              <g fill="none" fillRule="evenodd">
                <path
                  d="M5.866 44.963C2.65 44.958.002 42.317 0 39.105V6.21C.002 2.998 2.649.357 5.866.352h107.265c3.217.005 5.864 2.646 5.867 5.858v32.895c-.003 3.212-2.65 5.853-5.867 5.858H5.866z"
                  fill="#b8b8b8"
                ></path>
                <path
                  d="M5.866 1.267C3.151 1.27.918 3.5.916 6.21v32.895c.002 2.71 2.235 4.94 4.95 4.943H40.34V1.268H5.866zM87.022 31.04l.734-1.03c.528.551 1.26.861 2.024.858.745 0 1.108-.346 1.108-.705 0-.466-.592-.6-1.289-.77-1.04-.238-2.376-.52-2.376-1.973 0-1.057.917-1.934 2.415-1.934 1.012 0 1.852.305 2.483.885l-.755.994c-.51-.454-1.17-.701-1.853-.694-.582 0-.907.258-.907.638 0 .41.584.543 1.27.705 1.041.248 2.387.563 2.387 2.002 0 1.163-.83 2.04-2.549 2.04-1.224-.012-2.102-.416-2.692-1.016zm-5.802 0l.734-1.03c.528.551 1.26.861 2.024.858.745 0 1.108-.346 1.108-.705 0-.466-.592-.6-1.288-.77-1.042-.238-2.377-.52-2.377-1.973 0-1.057.916-1.934 2.415-1.934 1.012 0 1.852.305 2.483.885l-.75.994a2.743 2.743 0 00-1.853-.694c-.582 0-.906.258-.906.638 0 .41.583.543 1.27.705 1.04.248 2.386.563 2.386 2.002 0 1.163-.83 2.04-2.548 2.04-1.222-.012-2.1-.416-2.698-1.016zm-21.135 0l.735-1.03c.528.551 1.26.861 2.024.858.744 0 1.107-.346 1.107-.705 0-.466-.592-.6-1.288-.77-1.041-.238-2.377-.52-2.377-1.973 0-1.057.917-1.934 2.415-1.934 1.012 0 1.853.305 2.482.885l-.755.994a2.734 2.734 0 00-1.852-.694c-.581 0-.906.258-.906.638 0 .41.581.543 1.269.705 1.042.248 2.387.563 2.387 2.002 0 1.163-.83 2.04-2.549 2.04-1.222-.012-2.1-.416-2.692-1.016zm-6.642-1.64v-3.82h1.389v3.774c0 .886.52 1.496 1.498 1.496.977 0 1.49-.61 1.49-1.496V25.58h1.388v3.813c0 1.582-.936 2.659-2.873 2.659-1.937 0-2.892-1.087-2.892-2.65zm19.942 2.536l-3.037-4.145v4.145h-1.356V25.58h1.39l2.951 3.987v-3.987h1.356v6.357h-1.304zm-7.075 0V25.58h1.357v6.357H66.31zm9.71 0V25.58h4.514v1.191h-3.158v1.343h3.084v1.19h-3.084v1.44h3.15v1.19l-4.506.003zm-28.968 0V25.58h3.35c1.216 0 1.834.772 1.834 1.62 0 .801-.496 1.335-1.097 1.458.677.104 1.215.763 1.215 1.56 0 .953-.639 1.716-1.852 1.716l-3.45.003zm3.924-1.906c0-.4-.287-.743-.822-.743h-1.746v1.476h1.746c.511 0 .822-.279.822-.733zm-.123-2.6v-.001a.697.697 0 00-.746-.692h-1.699v1.386h1.7c.456 0 .743-.285.743-.686l.002-.006zm2.49-9.587c0-1.952 1.48-3.292 3.39-3.292a2.919 2.919 0 012.663 1.56l-1.165.571a1.726 1.726 0 00-1.49-.928c-1.154 0-1.994.885-1.994 2.08 0 1.193.84 2.079 1.994 2.079a1.71 1.71 0 001.498-.924l1.158.577c-.458.79-1.26 1.56-2.662 1.56-1.903 0-3.391-1.328-3.391-3.283zm6.627 0c0-1.952 1.48-3.292 3.389-3.292a2.919 2.919 0 012.663 1.56l-1.165.571a1.727 1.727 0 00-1.498-.928c-1.154 0-1.995.885-1.995 2.08 0 1.193.84 2.079 1.995 2.079a1.71 1.71 0 001.498-.924l1.165.562c-.458.79-1.26 1.56-2.663 1.56-1.907.015-3.389-1.313-3.389-3.268zm10.531 3.175l-1.25-2.253h-.993v2.253h-1.355v-6.356h2.979c1.326 0 2.138.867 2.138 2.048 0 1.125-.706 1.733-1.388 1.887l1.431 2.426-1.562-.005zm-19.058 0l-.39-1.076H48.32l-.4 1.076h-1.548l2.464-6.356h1.7l2.453 6.356h-1.547zm48.162 0v-6.356h2.51c1.995 0 3.38 1.267 3.38 3.184 0 1.916-1.389 3.172-3.38 3.172h-2.51zm-5.634 0v-6.356h4.514v1.19h-3.158v1.343h3.08v1.191h-3.08v1.438h3.15v1.19l-4.506.004zm-8.305 0v-6.356h1.356v6.356h-1.356zm-6.924 0v-6.356h2.511c1.995 0 3.38 1.267 3.38 3.184 0 1.916-1.39 3.172-3.38 3.172h-2.51zm-5.633 0v-6.356h4.514v1.19h-3.159v1.343h3.084v1.191h-3.084v1.438h3.152v1.19l-4.507.004zm16.71 0v-5.166h-1.863v-1.19h5.08v1.19h-1.853v5.166h-1.365zm14.3-3.172c0-1.125-.695-1.991-1.995-1.991h-1.155v3.971h1.155c1.253 0 1.987-.904 1.987-1.983l.007.003zm-20.863 0c0-1.125-.694-1.991-1.994-1.991h-1.155v3.971h1.155c1.251 0 1.987-.904 1.987-1.983l.007.003zm-33.569-1.832l-.993 2.734h1.986l-.993-2.735zm20.943.696c0-.533-.41-.858-.955-.858h-1.422v1.716h1.422c.55 0 .955-.324.955-.858z"
                  fill="#FFF"
                ></path>
                <path
                  d="M14.26 37.106c.905.402 1.35.667 1.522 1.788.292 1.958-1.513 2.702-3.218 2.702H8.571v-8.55h4.028c1.442 0 2.763.658 2.763 2.267 0 .805-.419 1.392-1.102 1.793zm7.997 0c.904.402 1.348.667 1.521 1.788.292 1.958-1.515 2.702-3.218 2.702h-3.993v-8.55h4.028c1.442 0 2.763.658 2.763 2.267 0 .805-.418 1.392-1.1 1.793zm9.516 1.79c-.174-1.122-.618-1.387-1.522-1.789.683-.4 1.104-.987 1.104-1.794 0-1.61-1.321-2.266-2.764-2.266h-4.028v8.549h3.993c1.703 0 3.51-.744 3.22-2.702l-.003.001zm1.112.654v-.017c0-1.08.89-1.97 1.974-1.97 1.081 0 1.971.887 1.974 1.965v.016c0 1.081-.89 1.971-1.974 1.971-1.081 0-1.971-.886-1.974-1.965zm3.769-.006v-.016a1.802 1.802 0 00-1.795-1.786c-.985 0-1.795.809-1.795 1.791v.017c.004.98.812 1.785 1.795 1.785.984 0 1.795-.808 1.795-1.79zm-2.57-1.072h.91c.22-.01.435.066.6.213a.531.531 0 01.163.392v.01c0 .327-.218.523-.524.594l.628.81h-.371l-.584-.761h-.518v.762h-.3l-.003-2.02zM12.728 39.98h-2.338v-1.95h2.338c1.717 0 1.611 1.95 0 1.95zm7.998-.001H18.39V38.03h2.333c1.716 0 1.611 1.95 0 1.95l.002-.002zm5.661-1.948v1.95h2.333c1.612 0 1.716-1.95 0-1.95h-2.333zm8.576 1.432c.284 0 .48-.13.48-.363v-.011c0-.212-.174-.348-.463-.348h-.595v.718l.578.004zm-24.573-4.78h2.096c1.308 0 1.354 1.823.082 1.823h-2.178v-1.823zm16.004-.002h2.085c1.308 0 1.355 1.823.082 1.823h-2.174l.007-1.823zm-8.001-.002h2.092c1.307 0 1.354 1.823.081 1.823H18.39l.002-1.823zm-5.958-5.457h3.894l.694 2.357h6.364l.695-2.357h3.895l-.51-1.733H12.944l-.51 1.733zM17.15 15.25l-1.887 2.6c-.946 1.3-.653 3.605.64 4.536l3.5 2.521c.6.434.646.867.362 1.319l.508.378 1.773-2.45c1.095-1.515 1-3.576-.625-4.756L17.89 16.83c-.422-.309-.467-.737-.233-1.213l-.508-.362.001-.005zm3.028-11.473L17.24 7.834c-1.378 1.903-.877 5.147 1.007 6.533l4.831 3.541c.98.717 1.108 2.546.394 3.481l.41.307 3.333-4.68c1.497-2.1 1.41-5.207-.94-6.91l-5.169-3.748c-.723-.52-1.085-1.416-.483-2.267l-.444-.314z"
                  fill="#b8b8b8"
                ></path>
              </g>
            </svg>
          </div>
          <div>
            <FairHousing
              alt="Equal Opportunity Housing Logo"
              src="https://imgdrop.imgix.net/2cfd25967a794adabcc97873553da5ab.png"
              height="24px"
              width="25px"
            />
          </div>
        </div>
      </RegularContainer>

      <RegularContainer style={{ marginTop: '20px', width: '100%', margin: 0 }}>
        {!props.hideLegalese && (
          <>
            <LegalP>
              Offer eligibility and purchase price vary. Featured customers may have been
              compensated for their time. Their opinions and views are solely their own.
            </LegalP>
            <br />
          </>
        )}
        <LegalP>
          Learn more about the{' '}
          <a href="https://www.opendoor.com/w/terms#brokerage_representation">
            Opendoor Brokerages
          </a>
        </LegalP>
        {props.addLegalese && (
          <>
            <LegalP>{props.addLegalese}</LegalP>
            <br />
          </>
        )}
      </RegularContainer>
    </FooterSection>
  );
};

export default Footer;
